import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
    Expand,
    Minimize2,
    Sun,
    Moon,
    Heart,
    Search,
    SlidersHorizontal,
    CalendarPlus,
} from 'lucide-react';
import api from '../../../../services/api';
import { useDynamicFilters } from '../../hooks/useDynamicFilters';
import type {
    BoardMenuApplyResult,
    DynamicFilterFieldValue,
    KanbanFilterColumn,
    KanbanFilterFieldSchema,
} from '../../types';
import {
    Bar,
    Btn,
    Overlay,
    Panel,
    PanelHandle,
    PanelTitle,
    PanelBody,
    SearchRow,
    SearchInputStyled,
    SearchBtn,
    FilterSectionLabel,
    CheckboxRow,
    FilterFooter,
    ApplyBtn,
    ClearBtn,
} from './styles';
import {
    loadKanbanFilterPreferences,
    saveKanbanFilterPreferences,
    clearKanbanFilterPreferences,
} from '../../utils/kanbanFilterPreferences';

interface BottomMenuBarProps {
    boardId: number;
    onThemeToggle: () => void;
    onMyCardsToggle: () => void;
    onSearchTermChange: (term: string) => void;
    onApplyFilters: (result: BoardMenuApplyResult) => void;
    onClearFilters: () => void;
    onZoomToggle?: () => void;
    isZoomedOut?: boolean;
    showMyCards: boolean;
    currentTheme: string;
    hasActiveFilters?: boolean;
    onOpenCreateFromSchedule?: () => void;
    canCreateFromSchedule?: boolean;
}

const BottomMenuBar: React.FC<BottomMenuBarProps> = ({
    boardId,
    onThemeToggle,
    onMyCardsToggle,
    onSearchTermChange,
    onApplyFilters,
    onClearFilters,
    onZoomToggle,
    isZoomedOut = false,
    showMyCards,
    currentTheme,
    hasActiveFilters = false,
    onOpenCreateFromSchedule,
    canCreateFromSchedule = false,
}) => {
    const [searchOpen, setSearchOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedColumns, setSelectedColumns] = useState<number[]>([]);
    const [fieldValues, setFieldValues] = useState<Record<string, DynamicFilterFieldValue>>({});
    const filterPrefsHydratedRef = useRef(false);

    const {
        columns,
        fields,
        loading: filtersLoading,
        buildPayload,
        getDefaultValues,
        normalizePersistedValues,
        isValueEmpty,
        FieldComponents,
    } = useDynamicFilters(boardId);

    useEffect(() => {
        filterPrefsHydratedRef.current = false;
    }, [boardId]);

    useEffect(() => {
        if (!boardId || filtersLoading || filterPrefsHydratedRef.current) return;
        const saved = loadKanbanFilterPreferences(boardId);
        filterPrefsHydratedRef.current = true;
        if (!saved) return;
        setSearchTerm(saved.search);
        setSelectedColumns(saved.selectedColumns);
        setFieldValues(normalizePersistedValues(saved.fieldValues));
        onSearchTermChange(saved.search);
    }, [boardId, filtersLoading, normalizePersistedValues, onSearchTermChange]);

    useEffect(() => {
        if (!filterOpen || filtersLoading) return;

        const saved = loadKanbanFilterPreferences(boardId);
        const defaults = getDefaultValues();

        if (saved) {
            setSearchTerm(saved.search);
            setSelectedColumns(saved.selectedColumns);
            setFieldValues(normalizePersistedValues(saved.fieldValues));
            onSearchTermChange(saved.search);
        } else {
            setFieldValues(defaults);
            setSelectedColumns([]);
        }
    }, [filterOpen, filtersLoading, boardId, getDefaultValues, normalizePersistedValues, onSearchTermChange]);

    const closeAll = useCallback(() => {
        setSearchOpen(false);
        setFilterOpen(false);
    }, []);

    const handleSearchSubmit = useCallback(() => {
        onSearchTermChange(searchTerm);
        setSearchOpen(false);
    }, [searchTerm, onSearchTermChange]);

    const handleColumnToggle = useCallback((id: number) => {
        setSelectedColumns(prev =>
            prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
        );
    }, []);

    const handleFieldChange = useCallback((key: string, value: DynamicFilterFieldValue) => {
        setFieldValues(prev => ({ ...prev, [key]: value }));
    }, []);

    const hasAnyFilter = useCallback(() => {
        if (selectedColumns.length > 0) return true;
        return Object.values(fieldValues).some(v => !isValueEmpty(v));
    }, [selectedColumns, fieldValues, isValueEmpty]);

    const handleClearLocalFilters = useCallback(() => {
        clearKanbanFilterPreferences(boardId);
        setSearchTerm('');
        setSelectedColumns([]);
        setFieldValues(getDefaultValues());
        onSearchTermChange('');
    }, [getDefaultValues, boardId, onSearchTermChange]);

    const handleApply = useCallback(async () => {
        const payload = buildPayload({
            search: searchTerm,
            selectedColumns,
            ...fieldValues,
        });

        if (process.env.NODE_ENV === 'development') {
            console.debug('[Kanban filter] apply payload', {
                board_id: payload.board_id,
                column_ids: payload.column_ids,
                filtersCount: payload.filters?.length ?? 0,
                search: payload.search,
            });
        }

        if (!payload.search && (!payload.filters || payload.filters.length === 0) && (!payload.column_ids || payload.column_ids.length === 0)) {
            return;
        }

        try {
            const response = await api.post('/api/adm/cards/search', payload);
            let cardsData: any[] = [];

            if (response.data?.success && response.data?.data) {
                cardsData = Array.isArray(response.data.data.data)
                    ? response.data.data.data
                    : Array.isArray(response.data.data)
                        ? response.data.data
                        : [];
            } else if (Array.isArray(response.data)) {
                cardsData = response.data;
            } else if (response.data?.cards && Array.isArray(response.data.cards)) {
                cardsData = response.data.cards;
            }

            onApplyFilters({ payload, cards: cardsData, rawResponse: response.data });
            saveKanbanFilterPreferences(boardId, {
                search: searchTerm,
                selectedColumns,
                fieldValues: fieldValues as Record<string, unknown>,
            });
            setFilterOpen(false);
        } catch (err) {
            onApplyFilters({ payload, cards: [], rawResponse: null });
            setFilterOpen(false);
        }
    }, [searchTerm, selectedColumns, fieldValues, buildPayload, onApplyFilters, boardId]);

    const panelOpen = searchOpen || filterOpen;

    return (
        <>
            {panelOpen && <Overlay onClick={closeAll} />}

            <Panel $open={searchOpen}>
                <PanelHandle />
                <PanelTitle>Pesquisar cards</PanelTitle>
                <PanelBody>
                    <SearchRow>
                        <SearchInputStyled
                            type="text"
                            placeholder="Pesquisar cards..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
                            autoFocus={searchOpen}
                        />
                        <SearchBtn onClick={handleSearchSubmit}>Buscar</SearchBtn>
                    </SearchRow>
                    {hasActiveFilters && (
                        <div style={{ marginTop: 12 }}>
                            <ClearBtn
                                style={{ width: '100%' }}
                                onClick={() => { onClearFilters(); setSearchOpen(false); }}
                            >
                                Limpar filtros ativos
                            </ClearBtn>
                        </div>
                    )}
                </PanelBody>
            </Panel>

            <Panel $open={filterOpen}>
                <PanelHandle />
                <PanelTitle>Filtros</PanelTitle>
                <PanelBody>
                    {filtersLoading && (
                        <p style={{ fontSize: '0.85rem', opacity: 0.6 }}>Carregando filtros...</p>
                    )}

                    {!filtersLoading && (
                        <>
                            {columns.length > 0 && (
                                <>
                                    <FilterSectionLabel>Colunas</FilterSectionLabel>
                                    {columns.map((col: KanbanFilterColumn) => (
                                        <CheckboxRow key={col.id}>
                                            <input
                                                type="checkbox"
                                                checked={selectedColumns.includes(col.id)}
                                                onChange={() => handleColumnToggle(col.id)}
                                            />
                                            {col.name}
                                        </CheckboxRow>
                                    ))}
                                </>
                            )}

                            {fields
                                .slice()
                                .sort((a: KanbanFilterFieldSchema, b: KanbanFilterFieldSchema) => a.order - b.order)
                                .map((field: KanbanFilterFieldSchema) => {
                                    const key = `field_${field.id}`;
                                    const FieldComponent = FieldComponents[key];
                                    if (!FieldComponent) return null;
                                    return (
                                        <FieldComponent
                                            key={field.id}
                                            field={field}
                                            value={fieldValues[key]}
                                            onChange={(value: DynamicFilterFieldValue) => handleFieldChange(key, value)}
                                        />
                                    );
                                })}
                        </>
                    )}
                </PanelBody>

                <FilterFooter>
                    {hasAnyFilter() && (
                        <ClearBtn onClick={handleClearLocalFilters}>Limpar</ClearBtn>
                    )}
                    <ApplyBtn onClick={handleApply}>Aplicar filtros</ApplyBtn>
                </FilterFooter>
            </Panel>

            <Bar>
                <Btn onClick={onZoomToggle} $active={isZoomedOut} title={isZoomedOut ? 'Zoom normal' : 'Zoom out'}>
                    {isZoomedOut ? (
                        <Minimize2 className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                        <Expand className="h-3.5 w-3.5" aria-hidden />
                    )}
                </Btn>

                <Btn onClick={onThemeToggle} title="Alternar tema">
                    {currentTheme === 'dark' ? (
                        <Sun className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                        <Moon className="h-3.5 w-3.5" aria-hidden />
                    )}
                </Btn>

                <Btn onClick={onMyCardsToggle} $active={showMyCards} title="Meus favoritos">
                    <Heart
                        className={`h-3.5 w-3.5 ${showMyCards ? 'fill-current' : ''}`}
                        aria-hidden
                    />
                </Btn>

                <Btn
                    onClick={() => { setFilterOpen(false); setSearchOpen(v => !v); }}
                    $active={searchOpen}
                    title="Buscar cards"
                >
                    <Search className="h-3.5 w-3.5" aria-hidden />
                </Btn>

                <Btn
                    onClick={() => { setSearchOpen(false); setFilterOpen(v => !v); }}
                    $active={filterOpen || hasActiveFilters}
                    title="Filtros"
                >
                    <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
                </Btn>

                {canCreateFromSchedule && (
                    <Btn
                        onClick={onOpenCreateFromSchedule}
                        title="Criar card a partir de agendamento"
                    >
                        <CalendarPlus className="h-3.5 w-3.5" aria-hidden />
                    </Btn>
                )}
            </Bar>
        </>
    );
};

export default BottomMenuBar;